import BaseService from './BaseService';
import { db } from '../../config/firebase';
import ConfigService from './ConfigService';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc, 
  onSnapshot 
} from 'firebase/firestore';
import deviceService from './DeviceService';
import firebaseService from './FirebaseService';

/**
 * Service for managing subscription-related operations
 */
class SubscriptionService extends BaseService {
  constructor() {
    super('stripe_products');
  }

  /**
   * Load all active subscription products with their prices
   */
  async loadSubscriptions(useCache = true) {
    try {
      // Use BaseService's cached getDocuments method instead of direct Firebase calls
      const data = await this.getDocuments({ active: true }, null, null, useCache);
      
      // Get prices for each product (this part still needs direct calls since it's subcollection)
      const pricesPromises = data.map(async (sub) => {
        const prices = [];
        const pricesSnapshot = await getDocs(collection(db, `stripe_products/${sub.key}/prices`));
        pricesSnapshot.forEach(price => {
          const priceData = price.data();
          priceData.key = price.id;
          prices.push(priceData);
        });
        return prices;
      });

      const prices = await Promise.all(pricesPromises);
      data.forEach((item, index) => item.prices = prices[index]);
      
      // Filter for recurring subscriptions only
      const subscriptionData = data.filter(sub => 
        sub.prices.length > 0 && 
        sub.prices[0].type === "recurring"
      );

      // Add hardcoded trial package
      const trialPackage = {
        metadata: {
          devices: ConfigService.getCurrentConfig().trial.devices,
          days: 14 // Trial days are still hardcoded for now
        },
        key: 'trial',
        name: 'Free Trial',
        prices: [{
          product: 'trial',
          unit_amount: 0,
          recurring: { interval: 'month' }
        }],
        product: {
          id: 'trial'
        },
        isTrial: true
      };

      // Combine Firebase data with hardcoded trial package
      const allSubscriptions = [...subscriptionData, trialPackage];

      // Sort subscriptions by price (ascending)
      const sortedSubscriptions = allSubscriptions.sort((a, b) => {
        const priceA = a.prices && a.prices.length > 0 ? a.prices[0].unit_amount : 0;
        const priceB = b.prices && b.prices.length > 0 ? b.prices[0].unit_amount : 0;
        return priceA - priceB;
      });

      return sortedSubscriptions;
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      throw error;
    }
  }

  /**
   * Load user devices
   */
  async loadUserDevices(userId) {
    try {
      // Use DeviceService instead of direct Firebase call for caching
      const userDevices = await deviceService.getUserDevices(userId, true); // useCache = true
      return userDevices;
    } catch (error) {
      console.error('Error loading user devices:', error);
      throw error;
    }
  }

  /**
   * Start a free trial for a user
   */
  async startTrial(userId, trialData) {
    try {
      const userRef = doc(db, 'users', userId);
      const trialInfo = {
        devices: trialData.devices,
        days: trialData.days,
        endDate: new Date().getTime() + trialData.days * 24 * 60 * 60 * 1000,
        expired: false
      };
      
      await updateDoc(userRef, { trialData: trialInfo });
      
      // Send email notification to admin
      await this.sendTrialStartEmail(userId, trialInfo);
      
      return trialInfo;
    } catch (error) {
      console.error('Error starting trial:', error);
      throw error;
    }
  }

  /**
   * Send email notification to admin when user starts trial
   */
  async sendTrialStartEmail(userId, trialInfo) {
    try {
      // Get user data for email content
      const userData = await this.getUserData(userId);
      if (!userData) {
        console.error('User data not found for trial start email');
        return;
      }

      const subject = 'New Trial Started - MyDigiSign';
      const text = `A new trial has been started by ${userData.displayName || userData.username} (${userData.username}). Trial details: ${trialInfo.days} days, ${trialInfo.devices} devices. End date: ${new Date(trialInfo.endDate).toLocaleDateString()}`;
      
      const html = {
        title: 'New Trial Started',
        text: `
          <p><strong>User:</strong> ${userData.displayName || 'N/A'} (${userData.username})</p>
          <p><strong>Trial Duration:</strong> ${trialInfo.days} days</p>
          <p><strong>Device Limit:</strong> ${trialInfo.devices} devices</p>
          <p><strong>Start Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>End Date:</strong> ${new Date(trialInfo.endDate).toLocaleDateString()}</p>
          <p><strong>User ID:</strong> ${userId}</p>
        `
      };

      await firebaseService.sendAdminEmail(subject, text, html);
    } catch (error) {
      console.error('Error sending trial start email:', error);
      // Don't throw error to avoid breaking the trial start process
    }
  }

  /**
   * Send email notification to admin when user cancels trial
   */
  async sendTrialCancelEmail(userId) {
    try {
      // Get user data for email content
      const userData = await this.getUserData(userId);
      if (!userData) {
        console.error('User data not found for trial cancel email');
        return;
      }

      const subject = 'Trial Cancelled - MyDigiSign';
      const text = `A trial has been cancelled by ${userData.displayName || userData.username} (${userData.username}). User ID: ${userId}`;
      
      const html = {
        title: 'Trial Cancelled',
        text: `
          <p><strong>User:</strong> ${userData.displayName || 'N/A'} (${userData.username})</p>
          <p><strong>Cancellation Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>User ID:</strong> ${userId}</p>
          <p><strong>Note:</strong> Trial will continue until the original end date</p>
        `
      };

      await firebaseService.sendAdminEmail(subject, text, html);
    } catch (error) {
      console.error('Error sending trial cancel email:', error);
      // Don't throw error to avoid breaking the cancellation process
    }
  }

  /**
   * Send email notification to admin when trial expires
   */
  async sendTrialExpiredEmail(userId) {
    try {
      // Get user data for email content
      const userData = await this.getUserData(userId);
      if (!userData) {
        console.error('User data not found for trial expired email');
        return;
      }

      const subject = 'Trial Expired - MyDigiSign';
      const text = `A trial has expired for ${userData.displayName || userData.username} (${userData.username}). User ID: ${userId}`;
      
      const html = {
        title: 'Trial Expired',
        text: `
          <p><strong>User:</strong> ${userData.displayName || 'N/A'} (${userData.username})</p>
          <p><strong>Expiration Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>User ID:</strong> ${userId}</p>
          <p><strong>Action Required:</strong> User may need follow-up for conversion</p>
        `
      };

      await firebaseService.sendAdminEmail(subject, text, html);
    } catch (error) {
      console.error('Error sending trial expired email:', error);
      // Don't throw error to avoid breaking the process
    }
  }

  /**
   * Get user data by ID
   */
  async getUserData(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDocs(query(collection(db, 'users'), where('uid', '==', userId)));
      
      if (!userSnap.empty) {
        const userData = userSnap.docs[0].data();
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  /**
   * Test email functionality (for development/testing purposes)
   */
  async testTrialEmails(userId) {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        throw new Error('User data not found');
      }

              // Test trial start email
        await this.sendTrialStartEmail(userId, {
          devices: ConfigService.getCurrentConfig().trial.devices,
          days: 10,
          endDate: new Date().getTime() + 10 * 24 * 60 * 60 * 1000
        });

      // Test trial cancel email
      await this.sendTrialCancelEmail(userId);

      // Test trial expired email
      await this.sendTrialExpiredEmail(userId);

      // Test trial upgraded email
      await this.sendTrialUpgradedEmail(userId);

      return { success: true, message: 'All trial emails sent successfully' };
    } catch (error) {
      console.error('Error testing trial emails:', error);
      throw error;
    }
  }

  /**
   * Create a checkout session for subscription upgrade
   */
  async createCheckoutSession(userId, priceKey, successUrl, cancelUrl) {
    try {
      const checkoutSessionRef = await addDoc(
        collection(db, "stripe_customers", userId, "checkout_sessions"),
        {
          price: priceKey,
          success_url: successUrl,
          cancel_url: cancelUrl,
          automatic_tax: true,
          allow_promotion_codes: true
        }
      );

      return checkoutSessionRef;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Listen to checkout session updates
   */
  listenToCheckoutSession(checkoutSessionRef, onUpdate) {
    return onSnapshot(checkoutSessionRef, (snap) => {
      const { error, url } = snap.data();
      onUpdate({ error, url });
    });
  }

  /**
   * Subscribe to a plan (creates checkout session and listens for updates)
   */
  async subscribe(userId, priceKey, successUrl, cancelUrl, callback) {
    try {
      // Create checkout session
      const checkoutSessionRef = await this.createCheckoutSession(userId, priceKey, successUrl, cancelUrl);
      
      // Listen for updates
      const unsubscribe = this.listenToCheckoutSession(checkoutSessionRef, ({ error, url }) => {
        if (error) {
          callback(false, error, userId);
        }
        if (url) {
          callback(true, url, userId);
        }
      });

      // Return unsubscribe function for cleanup
      return unsubscribe;
    } catch (error) {
      console.error('Error in subscribe:', error);
      callback(false, error, userId);
      throw error;
    }
  }

  /**
   * End user's trial when they purchase a paid subscription
   */
  async endTrial(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'trialData.expired': true,
        'trialData.endDate': new Date().getTime()
      });
      
      // Send email notification to admin about trial ending due to upgrade
      await this.sendTrialUpgradedEmail(userId);
      
      console.log('Trial ended for user:', userId);
    } catch (error) {
      console.error('Error ending trial:', error);
      throw error;
    }
  }

  /**
   * Send email notification to admin when user upgrades from trial
   */
  async sendTrialUpgradedEmail(userId) {
    try {
      // Get user data for email content
      const userData = await this.getUserData(userId);
      if (!userData) {
        console.error('User data not found for trial upgraded email');
        return;
      }

      const subject = 'Trial Upgraded to Paid - MyDigiSign';
      const text = `A trial user has upgraded to a paid subscription: ${userData.displayName || userData.username} (${userData.username}). User ID: ${userId}`;
      
      const html = {
        title: 'Trial Upgraded to Paid',
        text: `
          <p><strong>User:</strong> ${userData.displayName || 'N/A'} (${userData.username})</p>
          <p><strong>Upgrade Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>User ID:</strong> ${userId}</p>
          <p><strong>Status:</strong> Successfully converted from trial to paid</p>
        `
      };

      await firebaseService.sendAdminEmail(subject, text, html);
    } catch (error) {
      console.error('Error sending trial upgraded email:', error);
      // Don't throw error to avoid breaking the upgrade process
    }
  }

  /**
   * Cancel user's trial (mark as cancelled but keep until end date)
   */
  async cancelTrial(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'trialData.cancelled': true,
        'trialData.cancelledAt': new Date().getTime()
      });
      
      // Send email notification to admin
      await this.sendTrialCancelEmail(userId);
      
      console.log('Trial cancelled for user:', userId);
    } catch (error) {
      console.error('Error cancelling trial:', error);
      throw error;
    }
  }

  /**
   * Get subscription price in formatted string
   */
  getFormattedPrice(subscription) {
    if (subscription.prices && subscription.prices.length > 0) {
      const price = subscription.prices[0];
      return (price.unit_amount / 100).toFixed(2); // Convert cents to dollars
    }
    return '0.00';
  }

  /**
   * Get subscription interval
   */
  getInterval(subscription) {
    if (subscription.prices && subscription.prices.length > 0) {
      const price = subscription.prices[0];
      return price.recurring?.interval || 'month';
    }
    return 'month';
  }

  /**
   * Get subscription features list
   */
  getFeatures(subscription) {
    const features = [];
    
    // Handle trial package specifically
    if (subscription.isTrial) {
      features.push(
        `Up to ${subscription.metadata.devices} devices`,
        `${subscription.metadata.days} days free trial`,
        'All premium features included',
        'No credit card required',
        'Cancel anytime'
      );
      return features;
    }
    
    // Add "Ad-Free Experience" as the first feature for all paid packages (non-trial)
    features.push('✨ Ad-Free Experience');
    
    // Add AI-Powered Platform as a highlighted feature
    features.push('🤖 AI-Powered Platform');
    
    // Add device limit
    if (subscription.metadata?.devices) {
      const deviceCount = subscription.metadata.devices;
      if (deviceCount === 'unlimited' || parseInt(deviceCount) > 100) {
        features.push('Unlimited devices');
      } else {
        features.push(`Up to ${deviceCount} devices`);
      }
    }

    // Add other features based on metadata
    if (subscription.metadata?.templates) {
      features.push(`${subscription.metadata.templates} templates`);
    }

    if (subscription.metadata?.support) {
      features.push(`${subscription.metadata.support} support`);
    }

    if (subscription.metadata?.analytics) {
      features.push(`${subscription.metadata.analytics} analytics`);
    }

    if (subscription.metadata?.ai) {
      features.push('AI content generation');
    }

    if (subscription.metadata?.branding) {
      features.push('Custom branding');
    }

    if (subscription.metadata?.api) {
      features.push('API access');
    }

    if (subscription.metadata?.whiteLabel) {
      features.push('White-label solution');
    }

    // Add "All premium features included" to all products
    features.push('All premium features included');

    // Add screen types to all products
    features.push('Video and Dynamic Screens');

    // Add Advanced Scheduling to all products
    features.push('Advanced Scheduling');

    // Add default features if none specified
    if (features.length === 4) { // Only has "Ad-Free Experience", "All premium features included", screen types, and "Advanced Scheduling"
      features.push('Basic templates', 'Email support', 'Standard analytics');
    }

    return features;
  }

  /**
   * Check if user can start a trial
   */
  canStartTrial(userData, subscription) {
    if (!subscription.isTrial) return false;
    
    // Check if user is currently in trial
    const isCurrentlyInTrial = userData?.trialData && 
                              userData.trialData.endDate > new Date().getTime() && 
                              !userData.trialData.expired;
    
    // Check if user has ever used a trial before
    const hasUsedTrialBefore = this.hasUsedTrial(userData);
    
    return !isCurrentlyInTrial && !hasUsedTrialBefore;
  }

  /**
   * Check if user has used trial before
   */
  hasUsedTrial(userData) {
    return userData?.trialData && 
           (userData.trialData.endDate < new Date().getTime() || 
            userData.trialData.expired);
  }

  /**
   * Check if subscription is current plan
   */
  isCurrentPlan(subscription, userData, userSubscriptions, allSubscriptions = []) {
    // Handle trial package - check if user is in trial
    if (subscription.isTrial) {
      // Only show trial as current if there's no active paid subscription
      const hasActivePaidSubscription = userSubscriptions.some(sub => 
        sub.status === 'active' && 
        !this.isTrialSubscription(sub, userData)
      );
      
      if (hasActivePaidSubscription) {
        return false; // Trial is not current if there's an active paid subscription
      }
      
      return userData?.trialData && 
             userData.trialData.endDate > new Date().getTime() && 
             !userData.trialData.expired;
    }
    
    // For paid subscriptions, check if this is the most expensive active subscription
    const mostExpensive = this.getMostExpensiveActiveSubscription(userSubscriptions, allSubscriptions);
    
    if (mostExpensive && mostExpensive.plan.key === subscription.key) {
      return true;
    }
    
    // Fallback to exact match if no most expensive found.
    // Only subscriptions that still grant access count - an expired or canceled
    // subscription must not keep a plan marked as "current" forever.
    const exactMatch = userSubscriptions.find(sub =>
      sub.product?.id === subscription.key && this.isSubscriptionValid(sub)
    );
    return !!exactMatch;
  }

  /**
   * Check if a subscription is a trial subscription
   */
  isTrialSubscription(subscription, userData) {
    // Check if this subscription corresponds to a trial plan
    // This is a helper method to determine if a subscription is trial-related
    return false; // For now, assume all paid subscriptions are not trial
  }

  /**
   * Normalize a Stripe period timestamp into milliseconds.
   * The Stripe extension writes these as Firestore Timestamps, but older
   * documents hold unix seconds and some code paths hold milliseconds.
   */
  getPeriodEndMs(value) {
    if (!value) return null;

    // Firestore Timestamp instance
    if (typeof value.toMillis === 'function') {
      return value.toMillis();
    }

    // Plain object from a serialized Firestore Timestamp
    if (typeof value === 'object' && typeof value.seconds === 'number') {
      return value.seconds * 1000;
    }

    if (typeof value === 'number') {
      // Anything past this threshold is already in milliseconds
      return value > 1000000000000 ? value : value * 1000;
    }

    const parsed = new Date(value).getTime();
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Check whether a Stripe subscription still grants access
   * (active, or canceled but still inside the paid-for period)
   */
  isSubscriptionValid(sub) {
    if (!sub) return false;

    if (sub.status === 'active') {
      return true;
    }

    // Canceled subscriptions stay valid until the end of the current period
    if (sub.status === 'canceled') {
      const expiryMs = this.getPeriodEndMs(sub.current_period_end);
      return expiryMs !== null && expiryMs > new Date().getTime();
    }

    return false; // Exclude other statuses (past_due, incomplete, etc.)
  }

  /**
   * Check whether the user's access only comes from canceled subscriptions
   * that are still inside their paid period (no active subscription left)
   */
  isOnlyCanceledButValid(userSubscriptions, subscriptions) {
    const mostExpensive = this.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions);
    return !!mostExpensive &&
      mostExpensive.subscription.status === 'canceled' &&
      !userSubscriptions.some(sub => sub.status === 'active');
  }

  /**
   * Get the most expensive active subscription (including canceled but valid ones)
   */
  getMostExpensiveActiveSubscription(userSubscriptions, subscriptions) {
    if (!userSubscriptions || userSubscriptions.length === 0 || !subscriptions || subscriptions.length === 0) {
      return null;
    }

    // Filter active and canceled but valid subscriptions and find their corresponding plans
    const validSubscriptions = userSubscriptions
      .filter(sub => this.isSubscriptionValid(sub))
      .map(sub => {
        const plan = subscriptions.find(s => s.key === sub.product?.id);
        return { subscription: sub, plan };
      })
      .filter(item => item.plan && !item.plan.isTrial); // Exclude trial plans

    if (validSubscriptions.length === 0) {
      return null;
    }

    // Sort by price (descending) and return the most expensive. On equal price
    // prefer an active subscription over a canceled one, so resubscribing to
    // the same plan doesn't keep showing the old canceled subscription.
    const sortedSubscriptions = validSubscriptions.sort((a, b) => {
      const priceA = a.plan.prices && a.plan.prices.length > 0 ? a.plan.prices[0].unit_amount : 0;
      const priceB = b.plan.prices && b.plan.prices.length > 0 ? b.plan.prices[0].unit_amount : 0;
      if (priceB !== priceA) {
        return priceB - priceA; // Descending order (most expensive first)
      }
      return (a.subscription.status === 'canceled') - (b.subscription.status === 'canceled');
    });

    return sortedSubscriptions[0];
  }

  /**
   * Get current plan name
   */
  getCurrentPlanName(userData, userSubscriptions, subscriptions) {
    // Check if user has active paid subscription first (prioritize over trial)
    if (userSubscriptions.length > 0) {
      const mostExpensive = this.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions);
      if (mostExpensive) {
        return mostExpensive.plan.name || 'Active Plan';
      }
    }
    
    // Check if user is in trial (only if no active paid subscription)
    if (userData?.trialData && 
        userData.trialData.endDate > new Date().getTime() && 
        !userData.trialData.expired) {
      // Check if trial is canceled
      if (userData.trialData.cancelled) {
        return 'Trial (Canceled)';
      }
      return 'Free Trial';
    }
    
    // Default to Free Plan
    return 'Free Plan';
  }

  /**
   * Get status information for a subscription
   */
  getStatusInfo(subscription, userData, userSubscriptions, allSubscriptions = []) {
    const isCurrent = this.isCurrentPlan(subscription, userData, userSubscriptions, allSubscriptions);
    
    let icon, color, text;
    
    if (subscription.isTrial && isCurrent) {
      // Check if trial is canceled
      if (userData?.trialData?.cancelled) {
        icon = 'AlertCircle';
        color = 'bg-orange-100 text-orange-800';
        text = 'Trial Canceled';
      } else {
        icon = 'CheckCircle';
        color = 'bg-blue-100 text-blue-800';
        text = 'Active Trial';
      }
    } else if (subscription.isTrial) {
      icon = 'AlertCircle';
      color = 'bg-blue-50 text-blue-600';
      text = 'Trial';
    } else if (isCurrent) {
      icon = 'CheckCircle';
      color = 'bg-green-100 text-green-800';
      text = 'Active';
    } else {
      icon = 'XCircle';
      color = 'bg-gray-100 text-gray-800';
      text = 'Inactive';
    }
    
    return { icon, color, text, isCurrent };
  }

  /**
   * Get device limit for a user based on their subscription
   */
  getDeviceLimit(userData, userSubscriptions = [], subscriptions = []) {
    // Check for admin override first (highest priority)
    if (userData?.deviceOverride && userData.deviceOverride > 0) {
      return userData.deviceOverride;
    }

    // Check if user has active paid subscription first (prioritize over trial)
    if (userSubscriptions.length > 0 && subscriptions.length > 0) {
      const mostExpensive = this.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions);
      if (mostExpensive && mostExpensive.plan.metadata?.devices) {
        const deviceCount = mostExpensive.plan.metadata.devices;
        if (deviceCount === 'unlimited' || parseInt(deviceCount) > 100) {
          return Infinity;
        }
        return parseInt(deviceCount);
      }
    }
    
    // Check if user is in trial (only if no active paid subscription)
    if (userData?.trialData && 
        userData.trialData.endDate > new Date().getTime() && 
        !userData.trialData.expired) {
      return ConfigService.getCurrentConfig().trial.devices; // Trial users get devices from config
    }
    
    // Free users (including those without trial) get devices from config
    return ConfigService.getCurrentConfig().free.devices;
  }

  /**
   * Get screen limit for a user based on their subscription
   */
  getScreenLimit(userData, userSubscriptions = [], subscriptions = []) {
    // Check if user has active paid subscription first (prioritize over trial)
    if (userSubscriptions.length > 0 && subscriptions.length > 0) {
      const mostExpensive = this.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions);
      if (mostExpensive) {
        // For paid packages: screens = 2 × devices
        if (mostExpensive.plan.metadata?.devices) {
          const deviceCount = mostExpensive.plan.metadata.devices;
          if (deviceCount === 'unlimited' || parseInt(deviceCount) > 100) {
            return Infinity;
          }
          return parseInt(deviceCount) * 2; // 2 screens per device
        }
        // If no specific device limit, assume unlimited for paid plans
        return Infinity;
      }
    }
    
    // Check if user is in trial (only if no active paid subscription)
    if (userData?.trialData && 
        userData.trialData.endDate > new Date().getTime() && 
        !userData.trialData.expired) {
      return ConfigService.getCurrentConfig().trial.screens; // Trial users get screens from config
    }
    
    // Free users (including those without trial) get screens from config but no AI
    return ConfigService.getCurrentConfig().free.screens;
  }


}

export default new SubscriptionService(); 